import axios from "axios";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "/api").replace(
  /\/$/,
  "",
);
const configuredBackendOrigin = process.env.REACT_APP_BACKEND_ORIGIN;

const BACKEND_ORIGIN = (() => {
  if (configuredBackendOrigin)
    return configuredBackendOrigin.replace(/\/$/, "");
  if (/^https?:\/\//i.test(API_BASE_URL)) {
    try {
      return new URL(API_BASE_URL).origin;
    } catch {
      return window.location.origin;
    }
  }
  return window.location.origin;
})();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

const clearCustomerSession = () => {
  const hadToken = Boolean(localStorage.getItem("token"));
  localStorage.removeItem("token");
  localStorage.removeItem("user");

  if (hadToken && typeof window !== "undefined") {
    window.dispatchEvent(new Event("sugam:unauthorized"));
  }
};

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const payload = error.response?.data;
    const status = error.response?.status;
    const message = payload?.message || error.message || "";
    const requestUrl = String(error.config?.url || "");
    const isAuthEndpoint =
      requestUrl.includes("/login") || requestUrl.endsWith("/logout");

    if (status === 401 && !isAuthEndpoint) clearCustomerSession();
    return Promise.reject(payload || { message: message || "Request failed" });
  },
);

export const apiRequest = async (
  endpoint,
  method = "GET",
  data = null,
  headers = {},
) => {
  const requestHeaders = { ...headers };
  const isFormData =
    typeof FormData !== "undefined" && data instanceof FormData;

  if (isFormData) delete requestHeaders["Content-Type"];

  const response = await apiClient({
    url: endpoint,
    method,
    data,
    headers: requestHeaders,
    ...(isFormData
      ? {
          transformRequest: [
            (body, bodyHeaders) => {
              if (bodyHeaders && typeof bodyHeaders === "object") {
                delete bodyHeaders["Content-Type"];
              }
              return body;
            },
          ],
        }
      : {}),
  });

  return response.data;
};

export const getImageUrl = (image) => {
  if (typeof image !== "string" || !image) return "";
  if (/^(https?:|data:|blob:)/i.test(image)) return image;
  return `${BACKEND_ORIGIN}${image.startsWith("/") ? image : `/${image}`}`;
};

export const getProfileImage = (profileData) =>
  getImageUrl(profileData?.profile);

export const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getErrorMessage = (
  error,
  fallback = "Something went wrong. Please try again.",
) => error?.message || fallback;
